/** @jsxImportSource @emotion/react */
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import Toast from 'components/atoms/Toast'
import { UserPermission } from 'components/lab/user/permissions'
import { confirm } from 'components/molecules/Confirmation'
import { useLabPermission } from 'hooks/useLabPermission'
import { formatCalendarDate, formatEasternDateTime } from 'helpers/dates'
import React, { useState } from 'react'
import { Button } from 'react-bootstrap'
import {
  useGetChargeOptumClaimStatusHistoryQuery,
  useResolveChargeOptumClaimStatusMutation,
} from 'store/services/billingCharges'
import { BillingChargeDto, OptumClaimStatusSnapshot } from 'store/types'

type PayerRank = 'primary' | 'secondary' | 'tertiary'

// The verdicts are string enums on the backend; the frontend only ever reads
// them, so the literal union is enough and keeps runtime imports out.
type Status = OptumClaimStatusSnapshot['status']

const RANK_LABELS: Record<PayerRank, string> = {
  primary: 'Primary claim',
  secondary: 'Secondary claim (COB)',
  tertiary: 'Tertiary claim (COB)',
}

const STATUS_LABELS: Record<Status, string> = {
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected',
  PAID: 'Paid',
  DENIED: 'Denied',
}

// Blue when nothing is asked of us, red when the biller has to act, green for
// money, amber for a denial; a resolved rejection fades to grey. `separator`
// is the darker tone for the rules between detail rows, so they stay visible
// on each tinted background.
type Palette = { background: string; border: string; separator: string; text: string; icon: string }

const PALETTE: Record<Status | 'RESOLVED', Palette> = {
  ACCEPTED: {
    background: '#e0f0fd',
    border: '#b6dcfb',
    separator: '#7fb8ea',
    text: '#0b4a80',
    icon: 'fas fa-info-circle text-primary',
  },
  REJECTED: {
    background: '#fde0e0',
    border: '#f8b4b4',
    separator: '#dca1a1',
    text: '#7a1f1f',
    icon: 'fas fa-exclamation-circle text-danger',
  },
  PAID: {
    background: '#e0fde0',
    border: '#b5efb5',
    separator: '#6fbf6f',
    text: '#1d5b1d',
    icon: 'fas fa-check-circle text-success',
  },
  DENIED: {
    background: '#fff3cd',
    border: '#ffeeba',
    separator: '#d9b74a',
    text: '#856404',
    icon: 'fas fa-exclamation-triangle text-warning',
  },
  RESOLVED: {
    background: '#eef0f3',
    border: '#d9dde3',
    separator: '#aab1bd',
    text: '#4a5060',
    icon: 'fas fa-check text-secondary',
  },
}

// Only a verdict the biller may have to act on starts expanded. (Typed as
// strings: the backend enum is imported as a type only.)
const EXPANDED_BY_DEFAULT: string[] = ['REJECTED', 'DENIED']

const formatDate = (value?: string | null): string => formatCalendarDate(value, '—')

const formatMoney = (value?: number | null): string | null => (
  value === null || value === undefined ? null : `$${Number(value).toFixed(2)}`
)

/**
 * One line a biller can read without knowing X12: what the payer said and
 * whether they need to do anything about it.
 */
export const describeOptumClaimStatus = (snapshot: OptumClaimStatusSnapshot, needsReview: boolean): string => {
  const payer = snapshot.payerName || 'The payer'
  const on = ` on ${formatDate(snapshot.effectiveDate)}`

  switch (snapshot.status) {
    case 'REJECTED':
      return needsReview
        ? `${payer} rejected this claim${on}. Fix the claim and resubmit, or mark it resolved.`
        : `${payer} rejected this claim${on}. No review pending.`
    case 'PAID':
      return `${payer} finalized and paid this claim${on}.`
    case 'DENIED':
      return `${payer} finalized this claim${on} without payment.`
    case 'ACCEPTED':
    default:
      return snapshot.categoryCode?.startsWith('P')
        ? `${payer} has this claim in adjudication (pending) as of ${formatDate(snapshot.effectiveDate)}.`
        : `${payer} acknowledged this claim${on}. No action needed.`
  }
}

type BlockProps = {
  rank: PayerRank
  snapshot: OptumClaimStatusSnapshot
  payerNameOnCharge?: string | null
  needsReview: boolean
  showResolve: boolean
  resolving: boolean
  onResolve: () => void
  defaultOpen: boolean
}

const StatusBlock = ({
  rank, snapshot, payerNameOnCharge, needsReview, showResolve, resolving, onResolve, defaultOpen,
}: BlockProps) => {
  const [open, setOpen] = useState(defaultOpen)
  const isOpenRejection = snapshot.status === 'REJECTED' && needsReview
  const palette = PALETTE[snapshot.status === 'REJECTED' && !needsReview ? 'RESOLVED' : snapshot.status]
  const payerLabel = snapshot.payerName || payerNameOnCharge || '—'
  const codeLabel = [snapshot.categoryCode, snapshot.statusCode].filter(Boolean).join(':')
  const charged = formatMoney(snapshot.totalClaimChargeAmount)
  const paid = formatMoney(snapshot.claimPaymentAmount)

  return (
    <div
      data-testid={`optum-claim-status-${rank}`}
      css={{
        background: palette.background,
        border: `1px solid ${palette.border}`,
        color: palette.text,
        borderRadius: 5,
        padding: '10px 20px',
        marginBottom: 12,
      }}>
      <div className="d-flex align-items-center" css={{ gap: 10, '& > svg': { cursor: 'pointer' } }}>
        {open ? (
          <KeyboardArrowDownIcon onClick={() => setOpen(false)} />
        ) : (
          <KeyboardArrowUpIcon onClick={() => setOpen(true)} />
        )}
        <i className={`${palette.icon} mr-1`} />
        <span css={{ fontWeight: 600 }}>
          {RANK_LABELS[rank]} · {STATUS_LABELS[snapshot.status]}
          {snapshot.status === 'REJECTED' && !needsReview ? ' (resolved)' : ''}
        </span>
        <span css={{ opacity: 0.85 }}>
          {payerLabel} · {formatDate(snapshot.effectiveDate)}
          {codeLabel ? ` · ${codeLabel}` : ''}
        </span>
        <span css={{ flex: 1 }} />
        {isOpenRejection && showResolve && (
          <Button
            size="sm"
            variant="outline-danger"
            type="button"
            disabled={resolving}
            onClick={onResolve}>
            Mark as resolved
          </Button>
        )}
      </div>

      {open && (
        <div css={{ marginTop: 10, fontSize: 14 }}>
          <div css={{ fontWeight: 600, marginBottom: 6 }}>{describeOptumClaimStatus(snapshot, needsReview)}</div>
          <table
            className="table table-sm mb-2"
            css={{
              background: 'transparent',
              color: 'inherit',
              width: 'auto',
              minWidth: '60%',
              '& th, & td': { borderTop: `1px solid ${palette.separator}` },
            }}>
            <tbody>
              <tr>
                <th css={{ whiteSpace: 'nowrap', width: 180 }}>Payer</th>
                <td>{payerLabel}{snapshot.payerId ? ` (${snapshot.payerId})` : ''}</td>
              </tr>
              {snapshot.payerClaimNumber && (
                <tr>
                  <th>Claim Number</th>
                  <td>{snapshot.payerClaimNumber}</td>
                </tr>
              )}
              <tr>
                <th>Status category</th>
                <td>
                  {snapshot.categoryCode}
                  {snapshot.categoryDescription ? ` — ${snapshot.categoryDescription}` : ''}
                </td>
              </tr>
              {(snapshot.statusCode || snapshot.statusDescription) && (
                <tr>
                  <th>Status code</th>
                  <td>
                    {snapshot.statusCode}
                    {snapshot.statusDescription ? ` — ${snapshot.statusDescription}` : ''}
                    {snapshot.entityDescription ? ` (${snapshot.entityDescription})` : ''}
                  </td>
                </tr>
              )}
              {(snapshot.claimServiceBeginDate || snapshot.claimServiceEndDate) && (
                <tr>
                  <th>Service dates</th>
                  <td>
                    {formatDate(snapshot.claimServiceBeginDate)}
                    {snapshot.claimServiceEndDate && snapshot.claimServiceEndDate !== snapshot.claimServiceBeginDate
                      ? ` – ${formatDate(snapshot.claimServiceEndDate)}`
                      : ''}
                  </td>
                </tr>
              )}
              {(charged || paid) && (
                <tr>
                  <th>Amounts</th>
                  <td>
                    {charged ? `Charged ${charged}` : ''}
                    {charged && paid ? ' · ' : ''}
                    {paid ? `Paid ${paid}` : ''}
                  </td>
                </tr>
              )}
              <tr>
                <th>Received</th>
                <td>
                  {formatEasternDateTime(snapshot.receivedAt)}
                  {snapshot.reportFilename ? ` · ${snapshot.reportFilename}` : ''}
                </td>
              </tr>
            </tbody>
          </table>
          {snapshot.messages?.length > 0 && (
            <div>
              <div css={{ fontWeight: 600, marginBottom: 4 }}>Payer message{snapshot.messages.length > 1 ? 's' : ''}</div>
              <ul className="mb-0 pl-3">
                {snapshot.messages.map((message, index) => (
                  <li key={index} css={{ fontFamily: 'monospace', fontSize: 13, whiteSpace: 'pre-wrap' }}>
                    {message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

type HistoryProps = {
  patientId: string
  chargeId: string
}

const HistoryTable = ({ patientId, chargeId }: HistoryProps) => {
  const { data: history = [], isLoading } = useGetChargeOptumClaimStatusHistoryQuery({ patientId, chargeId })

  if (isLoading) {
    return <div className="text-muted font-italic">Loading payer responses…</div>
  }

  if (!history.length) {
    return <div className="text-muted font-italic">No payer responses have been received for this charge.</div>
  }

  return (
    <div css={{ overflowX: 'auto' }}>
      <table className="table table-sm mb-0" css={{ background: '#fff' }}>
        <thead>
          <tr>
            <th css={{ whiteSpace: 'nowrap' }}>Status date</th>
            <th>Claim</th>
            <th>Payer</th>
            <th>Status</th>
            <th>Codes</th>
            <th>Claim Number</th>
            <th>Message</th>
            <th css={{ whiteSpace: 'nowrap' }}>Received</th>
          </tr>
        </thead>
        <tbody>
          {history.map((row) => (
            <tr key={row.id} css={{ opacity: row.applied ? 1 : 0.65 }} title={row.skipReason ? `Not applied: ${row.skipReason}` : undefined}>
              <td css={{ whiteSpace: 'nowrap' }}>{formatDate(row.effectiveDate)}</td>
              <td>{row.payerRank ? RANK_LABELS[row.payerRank as PayerRank] : '—'}</td>
              <td>{row.payerName || '—'}</td>
              <td>{row.status ? STATUS_LABELS[row.status] : '—'}</td>
              <td css={{ whiteSpace: 'nowrap' }} title={[row.categoryDescription, row.statusDescription].filter(Boolean).join(' / ')}>
                {[row.categoryCode, row.statusCode].filter(Boolean).join(':') || '—'}
              </td>
              <td>{row.payerClaimNumber || '—'}</td>
              <td css={{ fontFamily: 'monospace', fontSize: 12, maxWidth: 420, whiteSpace: 'pre-wrap' }}>
                {row.messages.join('\n') || '—'}
              </td>
              <td css={{ whiteSpace: 'nowrap' }}>{formatEasternDateTime(row.receivedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

type Props = {
  charge: BillingChargeDto
  patientId: string
  onResolved?: () => void
}

/**
 * What the payers have said about this charge's claims, straight from the
 * Optum mailbox: one collapsible block per claim rank that has a status, the
 * open rejection first and expanded, everything else folded to its headline.
 */
export const ChargeOptumClaimStatusBanner = ({ charge, patientId, onResolved }: Props) => {
  const canResolve = useLabPermission(UserPermission.MODIFY_BILLING_CHARGES)
  const [resolveStatus, { isLoading: resolving }] = useResolveChargeOptumClaimStatusMutation()
  const [showHistory, setShowHistory] = useState(false)

  type Block = { rank: PayerRank; snapshot: OptumClaimStatusSnapshot; payerName?: string | null }
  const candidates: Array<{ rank: PayerRank; snapshot?: OptumClaimStatusSnapshot | null; payerName?: string | null }> = [
    { rank: 'primary', snapshot: charge.optumClaimStatus, payerName: charge.primaryPayerName },
    { rank: 'secondary', snapshot: charge.secondaryOptumClaimStatus, payerName: charge.secondaryPayerName },
    { rank: 'tertiary', snapshot: charge.tertiaryOptumClaimStatus, payerName: charge.tertiaryPayerName },
  ]
  const blocks = candidates.filter((block) => !!block.snapshot) as Block[]

  if (!blocks.length) {
    return null
  }

  // The review flag is charge-level: it is raised by a fixable rejection and
  // cleared once, however many ranks were rejected.
  const needsReview = !!charge.optumClaimStatusReview
  const openRejections = blocks.filter((b) => b.snapshot.status === 'REJECTED' && needsReview)
  const resolveRank = openRejections[0]?.rank

  const onResolve = async () => {
    // react-confirm resolves on "yes" and rejects on cancel.
    try {
      await confirm('', {
        title: 'Mark rejection as resolved?',
        description: 'The rejection stays on the charge for the record, but the charge leaves the "Rejected" list. Use this when the claim will not be resubmitted.',
        yesAction: 'Mark as resolved',
        noAction: 'Cancel',
        yesColor: 'danger',
      })
    } catch {
      return
    }

    try {
      await resolveStatus({ patientId, chargeId: charge.id }).unwrap()
      Toast({ type: 'success', label: 'Rejection marked as resolved' })
      onResolved?.()
    } catch (error: any) {
      Toast({ type: 'error', label: error?.data?.message || 'Failed to mark the rejection as resolved' })
    }
  }

  return (
    <div data-testid="optum-claim-status-banner" css={{ marginBottom: 8 }}>
      {blocks.map(({ rank, snapshot, payerName }) => (
        <StatusBlock
          key={rank}
          rank={rank}
          snapshot={snapshot}
          payerNameOnCharge={payerName}
          needsReview={needsReview}
          showResolve={canResolve && rank === resolveRank}
          resolving={resolving}
          onResolve={onResolve}
          defaultOpen={EXPANDED_BY_DEFAULT.includes(snapshot.status)}
        />
      ))}
      <div className="d-flex justify-content-end" css={{ marginTop: -4, marginBottom: 12 }}>
        <Button variant="link" size="sm" type="button" className="p-0" onClick={() => setShowHistory((x) => !x)}>
          {showHistory ? 'Hide payer response history' : 'Show payer response history'}
        </Button>
      </div>
      {showHistory && (
        <div css={{ background: '#eef0f3', borderRadius: 5, padding: 12, marginBottom: 15 }}>
          <HistoryTable patientId={patientId} chargeId={charge.id} />
        </div>
      )}
    </div>
  )
}
